import {Component, Input} from '@angular/core';
import {MarkdownComponent, provideMarkdown} from 'ngx-markdown';
import {StaticTextBlock, staticTextFormat} from '../../../shared/models/form-schema.model';

/**
 * A block of authored prose between the inputs of a form — a section heading, an explanation, a legal note.
 *
 * ### Two authoring formats, one visual result
 * Until #501 the content was always a raw HTML fragment pushed through `[innerHTML]`, and the block editor said
 * so ("Inhalt (HTML)"). An author who wanted a bullet list had to hand-write `<ul><li>`. The block may now
 * declare `format: 'markdown'` instead, which is converted by `ngx-markdown`.
 *
 * **The `html` branch is deliberately untouched, down to its class list.** Every form published before #501
 * stores no `format` at all, {@link staticTextFormat} resolves that to `html`, and the markup those forms render
 * has to stay byte-for-byte what it was — a form the customer proof-read last year must not re-flow because the
 * renderer grew a second branch. The Markdown branch carries the *same* `prose` classes so the two formats also
 * look alike, which is the point of offering both.
 *
 * ### Sanitisation
 * Both branches sanitise, and neither may opt out. `[innerHTML]` runs Angular's `DomSanitizer` in
 * `SecurityContext.HTML`; the Markdown branch goes through `ngx-markdown`, whose `MarkdownService` sanitises its
 * output with the same sanitiser in the same context (`provideMarkdown()` leaves `SANITIZE` at
 * `SecurityContext.HTML`, and `disableSanitizer` is left off). Definitions are authored by company admins rather
 * than by employees, but their content reaches every person who fills the form in.
 *
 * ### Why `provideMarkdown()` is declared here *as well as* in the app's root providers
 * `MarkdownService` is **not** `providedIn: 'root'` — it is a plain `@Injectable()` that something has to
 * provide, and its `SANITIZE` dependency is non-optional. #501 added `provideMarkdown()` to `APP_CONFIG`, which
 * is what the real app runs on (and which repaired the changelog page, whose `<markdown>` had no provider at
 * all). Declaring it on the component too costs one tiny service instance and makes the block self-contained:
 * it renders in a TestBed spec, in a Storybook story and inside the builder's live preview with no bootstrap
 * wiring, the same way {@link import('../../../submissions/field-note/field-note.component').FieldNoteComponent}
 * does. A renderer that needs the caller to remember a provider is a renderer that fails at the least convenient
 * moment.
 *
 * ### Change detection
 * Deliberately **default**, with a plain `@Input()` rather than a signal input. The block editor mutates the
 * very object it passes here (`[(ngModel)]="staticText().content"`) so its live preview updates as the author
 * types; an `OnPush` component behind an unchanged input reference would never re-render, and the preview would
 * silently show the previous keystroke.
 */
@Component({
  selector: 'forms-static-text',
  standalone: true,
  imports: [MarkdownComponent],
  providers: [provideMarkdown()],
  template: `
    @if (isMarkdown()) {
      <markdown class="static-text mb-8 block text-gray-800 leading-relaxed prose prose-sm max-w-none"
                [data]="block.content"></markdown>
    } @else {
      <div class="static-text mb-8 text-gray-800 leading-relaxed prose prose-sm max-w-none"
           [innerHTML]="block.content"></div>
    }
  `
})
export class StaticTextComponent {
  @Input() block!: StaticTextBlock;

  /**
   * Whether this block is authored as Markdown. Anything else — including a block that declares **no** format,
   * and a `format` value the schema does not know — is HTML, which is what keeps legacy forms unchanged.
   */
  isMarkdown(): boolean {
    return staticTextFormat(this.block) === 'markdown';
  }
}
